import React, { useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  DialogContent,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import {
  CloseRounded,
  LocalAtmOutlined,
  CreditCardOutlined,
  QrCode2Outlined,
  PrintOutlined,
  CheckCircle,
  DeliveryDining,
} from "@mui/icons-material";
import ReactToPrint from "react-to-print";
import { toast } from "react-toastify";
import { markOrderPaid, ITEM_STATUS } from "../../Store/Slices/restaurantSlice";
import { useUpdateOrderMutation } from "../../Store/Services/CheckoutService";
import { useGetAllCartItemsQuery } from "../../Store/Services/CartService";
import {
  clearSelectedCustomer,
  selectCustomer,
} from "../../Store/Slices/customerSlice";
import { getTableCartId } from "../../Utils/cartSession";
import {
  TAX_RATE,
  getItemTitle,
  getLineAmount,
  getOrderItems,
  summarizeOrderDetails,
} from "../../Utils/orderTotals";
import styles from "./SettleBillDialog.module.css";

const PAYMENT_MODES = [
  { id: "cash", label: "Cash", icon: <LocalAtmOutlined /> },
  { id: "card", label: "Card", icon: <CreditCardOutlined /> },
  { id: "upi", label: "UPI / Wallet", icon: <QrCode2Outlined /> },
];

const SettleBillDialog = ({ open, onClose, table, order }) => {
  const dispatch = useDispatch();
  const currency = useSelector((state) => state.currency);
  const { selectedCustomer } = useSelector(selectCustomer);
  const [updateOrder] = useUpdateOrderMutation();
  const symbol = currency?.currencySymbol || "";
  const receiptRef = useRef(null);
  const isOnline = !!table?.isOnline;
  const paymentModes = isOnline
    ? [
        {
          id: "prepaid",
          label: `Prepaid · ${table?.platform || "Platform"}`,
          icon: <DeliveryDining />,
        },
      ]
    : PAYMENT_MODES;
  const [paymentMode, setPaymentMode] = useState(
    isOnline ? "prepaid" : "cash",
  );
  const [cashReceived, setCashReceived] = useState("");
  const [completed, setCompleted] = useState(false);
  const [isSettling, setIsSettling] = useState(false);

  // The bill has to come from the server whenever the table has an open order
  // head - an order started on another terminal has no locally tracked items,
  // which previously rendered an empty bill with a zero total.
  const cartId =
    getTableCartId(table?.id) ||
    (table?.activeOrderHeadId ? String(table.activeOrderHeadId) : null);

  const { data: cartData, isFetching: isLoadingBill } = useGetAllCartItemsQuery(
    cartId,
    { skip: !cartId || !open, refetchOnMountOrArgChange: true }
  );

  const apiItems = useMemo(() => getOrderItems(cartData), [cartData]);
  const usesApiBill = apiItems.length > 0;

  const localItems = useMemo(() => order?.items || [], [order]);

  const billLines = useMemo(() => {
    if (usesApiBill) {
      return apiItems.map((line, index) => {
        const statusCode = String(line?.itemStatus || "").toUpperCase();

        return {
          key: line.orderDetId || `${getItemTitle(line)}-${index}`,
          title: getItemTitle(line),
          qty: Number(line?.qty) || 0,
          amount: getLineAmount(line),
          // A cancelled or refunded line is settled business, not outstanding.
          isServed:
            statusCode === "SERVED" ||
            ["CANCELLED", "REJECTED", "REFUNDED"].includes(statusCode),
        };
      });
    }

    return localItems.map((line) => ({
      key: line.lineId,
      title: line.title,
      qty: line.qty,
      amount: line.qty * line.price * (1 - (line.discount || 0) / 100),
      isServed: line.status === ITEM_STATUS.SERVED,
    }));
  }, [usesApiBill, apiItems, localItems]);

  const totals = useMemo(() => {
    if (usesApiBill) {
      return summarizeOrderDetails(cartData);
    }

    const subtotal = localItems.reduce(
      (sum, line) =>
        sum + line.qty * line.price * (1 - (line.discount || 0) / 100),
      0
    );
    const tax = subtotal * TAX_RATE;
    const qty = localItems.reduce((sum, line) => sum + line.qty, 0);
    return { subtotal, tax, total: subtotal + tax, qty };
  }, [usesApiBill, cartData, localItems]);

  const pendingCount = billLines.filter((line) => !line.isServed).length;

  const received = parseFloat(cashReceived) || 0;
  const change = received - totals.total;

  const resolveCustomerId = () => {
    const fromState =
      selectedCustomer?.custId ||
      selectedCustomer?.customerId ||
      selectedCustomer?.id;

    if (fromState) {
      return fromState;
    }

    if (typeof window === "undefined") {
      return null;
    }

    try {
      const stored = sessionStorage.getItem("posCustomer");
      if (!stored) {
        return null;
      }
      const parsed = JSON.parse(stored);
      return parsed?.custId || null;
    } catch {
      return null;
    }
  };

  const handleComplete = async () => {
    if (paymentMode === "cash" && received < totals.total) {
      toast.error("Cash received is less than the total.", {
        position: "bottom-right",
      });
      return;
    }

    if (!cartId) {
      toast.error("No open order found for this table.", {
        position: "bottom-right",
      });
      return;
    }

    setIsSettling(true);

    try {
      await updateOrder({
        orderHeadId: cartId,
        paymentMethod: paymentMode === "cash" ? "cash" : "card",
        orderType: 2,
        custId: resolveCustomerId(),
      }).unwrap();
    } catch (error) {
      setIsSettling(false);
      toast.error("Unable to complete payment right now. Please try again.", {
        position: "bottom-right",
      });
      return;
    }

    setIsSettling(false);

    // Payment only. The order deliberately stays open - and its cart id stays
    // mapped to the table - so serving can finish; Complete Order on the order
    // panel is what closes it and frees the table.
    dispatch(
      markOrderPaid({
        tableId: table.id,
        paymentMethod: paymentMode,
        paidTotal: Number(totals.total.toFixed(2)),
        orderHeadId: cartId,
      })
    );
    setCompleted(true);

    if (typeof window !== "undefined") {
      sessionStorage.removeItem("posCustomer");
    }
    dispatch(clearSelectedCustomer());
    window.dispatchEvent(
      new CustomEvent("pos:customer-changed", { detail: { customer: null } })
    );
    window.dispatchEvent(
      new CustomEvent("pos:invoice-completed", {
        detail: {
          invoiceId: cartId,
          total: Number(totals.total.toFixed(2)),
          paymentMethod: paymentMode === "cash" ? "Cash" : "Card",
          itemsCount: totals.qty,
          timestamp: new Date().toISOString(),
        },
      })
    );

    toast.success(
      isOnline
        ? `${table?.name} paid`
        : `Table ${table?.name} bill paid`,
      {
        position: "bottom-right",
      },
    );
  };

  const handleClose = () => {
    setPaymentMode(isOnline ? "prepaid" : "cash");
    setCashReceived("");
    setCompleted(false);
    setIsSettling(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ className: styles.paper, sx: { width: "100%", maxWidth: 820 } }}
    >
      <DialogContent className={styles.dialogContent}>
        <div className={styles.header}>
          <div>
            <Typography className={styles.title}>
              {completed ? "Payment Complete" : "Settle Bill"}
            </Typography>
            <Typography className={styles.subtitle}>
              {isOnline ? table?.name : `Table ${table?.name}`}
            </Typography>
          </div>
          <IconButton onClick={handleClose} size="small">
            <CloseRounded />
          </IconButton>
        </div>

        {completed ? (
          <div className={styles.successBox}>
            <CheckCircle className={styles.successIcon} />
            <Typography className={styles.successText}>
              Payment received for{" "}
              {isOnline ? table?.name : `Table ${table?.name}`}
            </Typography>
            <Typography className={styles.successSub}>
              Paid {symbol} {totals.total.toFixed(2)} via {paymentMode.toUpperCase()}
            </Typography>
            <Typography className={styles.successSub}>
              {pendingCount > 0
                ? `The order stays open — ${pendingCount} item${
                    pendingCount === 1 ? "" : "s"
                  } still to serve. Close it with Complete Order.`
                : "All items are served. Close the order with Complete Order to free the table."}
            </Typography>
            <div className={styles.actionRow}>
              <ReactToPrint
                trigger={() => (
                  <button className={styles.printButton}>
                    <PrintOutlined fontSize="small" /> Print Bill
                  </button>
                )}
                content={() => receiptRef.current}
              />
              <button className={styles.completeButton} onClick={handleClose}>
                Done
              </button>
            </div>
          </div>
        ) : (
          <>
            {pendingCount > 0 && (
              <div className={styles.warnBanner}>
                {pendingCount} item{pendingCount === 1 ? "" : "s"} not served
                yet. Taking payment now keeps the order open — close it with
                Complete Order once everything has been served.
              </div>
            )}

            <div className={styles.billItems}>
              {billLines.length === 0 ? (
                <div className={styles.billRow}>
                  <span className={styles.billName}>
                    {isLoadingBill
                      ? "Loading order details…"
                      : "No items on this order."}
                  </span>
                </div>
              ) : (
                billLines.map((line) => (
                  <div key={line.key} className={styles.billRow}>
                    <span className={styles.billQty}>{line.qty}×</span>
                    <span className={styles.billName}>{line.title}</span>
                    <span className={styles.billAmt}>
                      {symbol} {line.amount.toFixed(2)}
                    </span>
                  </div>
                ))
              )}
            </div>

            <div className={styles.totalsBox}>
              <div className={styles.totalLine}>
                <span>Subtotal ({totals.qty} items)</span>
                <span>
                  {symbol} {totals.subtotal.toFixed(2)}
                </span>
              </div>
              <div className={styles.totalLine}>
                <span>{usesApiBill ? "Tax" : "Tax (5%)"}</span>
                <span>
                  {symbol} {totals.tax.toFixed(2)}
                </span>
              </div>
              {usesApiBill && totals.charges > 0 && (
                <div className={styles.totalLine}>
                  <span>Charges</span>
                  <span>
                    {symbol} {totals.charges.toFixed(2)}
                  </span>
                </div>
              )}
              {usesApiBill && totals.offer > 0 && (
                <div className={styles.totalLine}>
                  <span>Discount</span>
                  <span>
                    − {symbol} {totals.offer.toFixed(2)}
                  </span>
                </div>
              )}
              {usesApiBill && totals.coupon > 0 && (
                <div className={styles.totalLine}>
                  <span>Coupon</span>
                  <span>
                    − {symbol} {totals.coupon.toFixed(2)}
                  </span>
                </div>
              )}
              <div className={styles.grandLine}>
                <span>Total</span>
                <span>
                  {symbol} {totals.total.toFixed(2)}
                </span>
              </div>
            </div>

            <Typography className={styles.sectionLabel}>Payment Mode</Typography>
            <div className={styles.modeRow}>
              {paymentModes.map((mode) => (
                <button
                  key={mode.id}
                  className={`${styles.modeButton} ${
                    paymentMode === mode.id ? styles.modeButtonActive : ""
                  }`}
                  onClick={() => setPaymentMode(mode.id)}
                >
                  {mode.icon}
                  <span>{mode.label}</span>
                </button>
              ))}
            </div>

            {isOnline && (
              <div className={styles.warnBanner}>
                This order is already paid through {table?.platform || "the platform"}.
                No cash to collect.
              </div>
            )}

            {paymentMode === "cash" && (
              <div className={styles.cashBox}>
                <TextField
                  label="Cash Received"
                  type="number"
                  size="small"
                  value={cashReceived}
                  onChange={(e) => setCashReceived(e.target.value)}
                  className={styles.cashInput}
                  InputProps={{ startAdornment: <span style={{ marginRight: 6 }}>{symbol}</span> }}
                />
                <div className={styles.changeRow}>
                  <span>Change Due</span>
                  <span className={change < 0 ? styles.changeNeg : styles.changePos}>
                    {symbol} {(change > 0 ? change : 0).toFixed(2)}
                  </span>
                </div>
              </div>
            )}

            <div className={styles.actionRow}>
              <ReactToPrint
                trigger={() => (
                  <button className={styles.printButton}>
                    <PrintOutlined fontSize="small" /> Print Bill
                  </button>
                )}
                content={() => receiptRef.current}
              />
              <button
                className={styles.completeButton}
                onClick={handleComplete}
                disabled={isSettling || isLoadingBill || billLines.length === 0}
              >
                <CheckCircle fontSize="small" />{" "}
                {isSettling ? "Processing…" : "Complete Order"}
              </button>
            </div>
          </>
        )}

        {/* Hidden printable receipt */}
        <div className={styles.printArea}>
          <div ref={receiptRef} className={styles.receipt}>
            <h2 className={styles.receiptTitle}>Restaurant Bill</h2>
            <p className={styles.receiptMeta}>
              {isOnline ? table?.name : `Table ${table?.name}`}
            </p>
            <p className={styles.receiptMeta}>
              {new Date().toLocaleString()}
            </p>
            <hr />
            {billLines.map((line) => (
              <div key={line.key} className={styles.receiptRow}>
                <span>
                  {line.qty} × {line.title}
                </span>
                <span>
                  {symbol} {line.amount.toFixed(2)}
                </span>
              </div>
            ))}
            <hr />
            <div className={styles.receiptRow}>
              <span>Subtotal</span>
              <span>
                {symbol} {totals.subtotal.toFixed(2)}
              </span>
            </div>
            <div className={styles.receiptRow}>
              <span>{usesApiBill ? "Tax" : "Tax (5%)"}</span>
              <span>
                {symbol} {totals.tax.toFixed(2)}
              </span>
            </div>
            <div className={styles.receiptTotal}>
              <span>Total</span>
              <span>
                {symbol} {totals.total.toFixed(2)}
              </span>
            </div>
            <p className={styles.receiptFooter}>
              Paid via {paymentMode.toUpperCase()} — Thank you!
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SettleBillDialog;
